import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Search, 
  Calendar, 
  Users, 
  Clock, 
  MapPin,
  Edit, 
  Trash2, 
  Eye,
  BookOpen,
  Award
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useMultipleCodelists } from '../hooks/useCodelist';
import { api } from '../lib/api';
import TrainingForm from '../components/trainings/TrainingForm';
import { Training } from '../types';

const TrainingsPage: React.FC = () => {
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [selectedTraining, setSelectedTraining] = useState<Training | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'view'>('create');
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();
  
  // Load codelists for display names
  const { getItemName } = useMultipleCodelists(['nis2_chapters', 'training_types']);

  // Load trainings on component mount
  useEffect(() => {
    loadTrainings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTrainings = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ success: boolean; data: Training[] }>('/trainings');
      if (response.success) {
        setTrainings(response.data);
      }
    } catch (error) {
      console.error('Failed to load trainings:', error);
      toast({
        title: "Error",
        description: "Failed to load trainings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTraining = async (trainingId: number) => {
    if (!window.confirm('Are you sure you want to delete this training?')) {
      return;
    }

    try {
      const response = await api.delete<{ success: boolean; message: string }>(`/trainings/${trainingId}`);
      if (response.success) {
        toast({
          title: "Success",
          description: "Training deleted successfully",
        });
        loadTrainings(); // Reload the list
      }
    } catch (error) {
      console.error('Failed to delete training:', error);
      toast({
        title: "Error",
        description: "Failed to delete training",
        variant: "destructive",
      });
    }
  };

  const handleCreateTraining = () => {
    setSelectedTraining(null);
    setFormMode('create');
    setShowForm(true);
  };

  const handleEditTraining = (training: Training) => {
    setSelectedTraining(training);
    setFormMode('edit');
    setShowForm(true);
  };

  const handleViewTraining = (training: Training) => {
    setSelectedTraining(training);
    setFormMode('view');
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedTraining(null);
  };

  const handleFormSave = () => {
    loadTrainings(); // Reload trainings after save
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'planned': return 'default';
      case 'ongoing': return 'secondary';
      case 'completed': return 'outline';
      case 'cancelled': return 'destructive';
      default: return 'default';
    }
  };

  const getCategoryBadgeVariant = (category: string) => {
    switch (category) {
      case 'mandatory': return 'destructive';
      case 'specialized': return 'default';
      case 'optional': return 'secondary';
      default: return 'secondary';
    }
  };

  const getLocationIcon = (location: string) => {
    switch (location) {
      case 'online': return '🌐';
      case 'on-site': return '🏢';
      case 'hybrid': return '🔄';
      default: return '📍';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('cs-CZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isUpcoming = (training: Training) => {
    const trainingDate = new Date(training.training_date);
    const now = new Date();
    const daysDiff = Math.ceil((trainingDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysDiff <= 7 && daysDiff >= 0 && training.status === 'planned';
  };

  const filteredTrainings = trainings.filter(training => {
    const matchesSearch = training.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         training.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         training.instructor_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === '' || training.type === selectedType;
    const matchesStatus = selectedStatus === '' || training.status === selectedStatus;
    const matchesLocation = selectedLocation === '' || training.location === selectedLocation;
    
    return matchesSearch && matchesType && matchesStatus && matchesLocation;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Training Management</h1>
          <p className="text-muted-foreground">
            Manage security trainings and track participant completion
          </p>
        </div>
        <Button onClick={handleCreateTraining}>
          <Plus className="h-4 w-4 mr-2" />
          Add Training
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search trainings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="">All Types</option>
                <option value="security_awareness">Security Awareness</option>
                <option value="technical">Technical</option>
                <option value="compliance">Compliance</option>
                <option value="incident_response">Incident Response</option>
                <option value="other">Other</option>
              </select>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="">All Statuses</option>
                <option value="planned">Planned</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="">All Locations</option>
                <option value="online">Online</option>
                <option value="on-site">On-site</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trainings Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredTrainings.map((training) => (
          <Card key={training.id} className={`hover:shadow-md transition-shadow ${isUpcoming(training) ? 'ring-2 ring-blue-200' : ''}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <BookOpen className="h-4 w-4" />
                  <CardTitle className="text-lg line-clamp-1">{training.title}</CardTitle>
                </div>
                <Badge variant={getStatusBadgeVariant(training.status)}>
                  {training.status}
                </Badge>
              </div>
              <CardDescription className="line-clamp-2">
                {training.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Category:</span>
                  <Badge variant={getCategoryBadgeVariant(training.category)}>
                    {training.category}
                  </Badge>
                </div>
                
                {training.training_date && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Calendar className="h-3 w-3 text-muted-foreground" />
                    <span>{formatDate(training.training_date)}</span>
                    {isUpcoming(training) && (
                      <Badge variant="secondary" className="text-xs">
                        Upcoming
                      </Badge>
                    )}
                  </div>
                )}

                <div className="flex items-center space-x-2 text-sm">
                  <Clock className="h-3 w-3 text-muted-foreground" />
                  <span>{training.duration_hours}h</span>
                </div>

                <div className="flex items-center space-x-2 text-sm">
                  <MapPin className="h-3 w-3 text-muted-foreground" />
                  <span>{getLocationIcon(training.location)} {training.location}</span>
                </div>

                <div className="flex items-center space-x-2 text-sm">
                  <Users className="h-3 w-3 text-muted-foreground" />
                  <span>
                    {training.participant_count}
                    {training.max_participants && ` / ${training.max_participants}`}
                  </span>
                </div>

                {training.instructor_name && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Instructor:</span>
                    <span className="text-right">{training.instructor_name}</span>
                  </div>
                )}

                {training.certificate_provided && (
                  <div className="flex items-center space-x-1 text-sm text-green-600">
                    <Award className="h-3 w-3" />
                    <span>Certificate provided</span>
                  </div>
                )}

                {training.nis2_chapter && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">NIS2:</span>
                    <span className="text-right">{getItemName('nis2_chapters', training.nis2_chapter)}</span>
                  </div>
                )}

                {training.completion_stats && training.status === 'completed' && (
                  <div className="bg-muted p-2 rounded text-xs">
                    <div className="flex justify-between">
                      <span>Completed:</span>
                      <span>{training.completion_stats.completed_participants}/{training.completion_stats.total_participants}</span>
                    </div>
                    {training.completion_stats.certificates_issued > 0 && (
                      <div className="flex justify-between">
                        <span>Certificates:</span>
                        <span>{training.completion_stats.certificates_issued}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-1 mt-4">
                <Button variant="outline" size="sm" onClick={() => handleViewTraining(training)}>
                  <Eye className="h-3 w-3" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleEditTraining(training)}>
                  <Edit className="h-3 w-3" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => training.id && handleDeleteTraining(training.id)}
                  disabled={!training.id}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTrainings.length === 0 && !loading && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No trainings found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || selectedType || selectedStatus || selectedLocation
                  ? "No trainings match your current filters."
                  : "Get started by scheduling your first security training."}
              </p>
              {!searchTerm && !selectedType && !selectedStatus && !selectedLocation && (
                <Button onClick={handleCreateTraining}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Training
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Training Form Modal */}
      <TrainingForm
        training={selectedTraining || undefined}
        isOpen={showForm}
        onClose={handleFormClose}
        onSave={handleFormSave}
        mode={formMode}
      />
    </div>
  );
};

export default TrainingsPage;
